import { useEffect, useMemo, useState } from 'react';
import isEqual from 'react-fast-compare';
import { BoundingBox, Mol, Reaction, CommonPointProps } from '@moleculeone/types';

import { styled } from '../../../stitches.config';
import { Checkbox } from '../../controls/Checkbox/Checkbox';
import { SynthesisTree } from '../SynthesisTree/SynthesisTree';
import { Button } from '../../controls/Button/Button';
import { Slider } from '../../controls/Slider/Slider';
import { ButtonCopy } from '../../controls/Button/ButtonCopy';

import { ExpandButton } from './components/ExpandButton';
import { StartSearchFromLeaf } from './components/StartSearchFromLeaf';
import { PathButtonGroup } from './components/PathButtonGroup';
import { getReaction } from './utils/getReactionSmiles';
import { pickTreeFromHypertree } from './utils/pickTreeFromHypertree';
import { References } from './components/References';
import { Pricing } from './components/Pricing/Pricing';
import { CompoundPricing } from './components/Pricing/Pricing.types';
import { SubstratePrice } from './components/PriceButton/SubstratePrice';
import { RootPrice } from './components/PriceButton/RootPrice';

const MOL_CONTAINER_PADDING = 90;

export interface HypertreeData {
  target: string;
  nodes: Record<
    string,
    {
      type: 'cmpd' | 'rxn';
      id: string;
      pred: string | null;
      succ: string[];
      score: number | null;
      compound_to_map: number[];
    }
  >;
  cmpds: Record<
    string,
    {
      smiles: string;
      is_sm: boolean;
      quotes: {
        dataset_id: string;
        price: number;
        amount: number;
        availability: number;
      }[];
    }
  >;
  rxns: Record<
    string,
    {
      substrates_to_map: number[][];
      product_to_map: number[];
      prediction: number | null;
    }
  >;
}

export interface Node {
  id: string;
  type: 'compound' | 'reaction';
  predecessor: string | null;
  successors: string[] | null;
  smiles?: string;
  productToMap?: number[];
  substratesToMap?: number[][];
}

const Container = styled('div', {
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
  paddingBottom: '$6',
});

const TreeTools = styled('div', {
  fontSize: '$2',
  display: 'flex',
  alignItems: 'center',
  gap: '$6',
  padding: '0 $5',
});

const ZoomControls = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: '$3',
});

const Nav = styled('div', {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '$4',
});

const TreeOverflowContainer = styled('div', {
  overflow: 'auto',
  width: '100%',
});

const TreeContainer = styled('div', {
  paddingTop: '$3',
  paddingRight: '$9',
  paddingLeft: '$9',
});

const ErrorMessage = styled('div', {
  color: '$red11',
  fontSize: '$2',
  fontWeight: 'bold',
  textAlign: 'center',
  padding: '$4',
});

const ReactionCommonPointButtons = styled('div', {
  display: 'flex',
  gap: '$2',
  justifyContent: 'center',
  alignItems: 'center',
});

interface HypertreeProps {
  hypertree: HypertreeData;
  selectedSyntheses?: Array<Record<string, Node>>;
  productToReactionMap: Record<string, number>;
  expandedNodes: string[];
  onChangeSelection?: (selectedSyntheses: Array<Record<string, Node>>) => void;
  onChangeProductToReactionMap: (productToReactionMap: Record<string, number>) => void;
  onChangeExpandedNodes: (expandedNodes: string[]) => void;
  smilesToMol?: (smiles: string) => Mol;
  scifinderToken?: string;
  rrConfig?: Record<string, string>;
  expandAll?: boolean;
  getPricing?: (smiles: string) => Promise<CompoundPricing[]>;
  debugEnabled?: boolean;
  onStartSearchFromLeaf?: (smiles: string) => void;
}

export function Hypertree(props: HypertreeProps) {
  const {
    hypertree,
    smilesToMol,
    selectedSyntheses = [],
    productToReactionMap,
    expandedNodes,
    onChangeSelection,
    onChangeProductToReactionMap,
    onChangeExpandedNodes,
    scifinderToken,
    rrConfig,
    debugEnabled = false,
    getPricing,
    onStartSearchFromLeaf,
  } = props;

  const [expandAll, setExpandAll] = useState(props.expandAll ?? false);
  const [zoom, setZoom] = useState(1);
  const [parsingError, setParsingError] = useState<string | null>(null);
  const [lastUpdatedReactionSelection, setLastUpdatedReactionSelection] = useState('');
  const [viewedCompoundPricing, setViewedCompoundPricing] = useState<any | null>(null);

  const { nodes, collapsedNodeIds } = useMemo(() => {
    try {
      return pickTreeFromHypertree(hypertree, productToReactionMap, expandedNodes, expandAll);
    } catch (error) {
      console.log(error);
      setParsingError(error instanceof Error ? error.message : (error as string));
      return { nodes: {}, collapsedNodeIds: [] };
    }
  }, [hypertree, productToReactionMap, expandedNodes, expandAll]);

  const isTreeRenderable = Object.keys(nodes).length > 0;
  const isSynthesisSelected = isTreeRenderable && selectedSyntheses.some(synthesis => isEqual(synthesis, nodes));

  const toggleActive = () => {
    const nextSelection = isSynthesisSelected
      ? selectedSyntheses.filter(synthesis => !isEqual(synthesis, nodes))
      : [...selectedSyntheses, nodes];
    onChangeSelection?.(nextSelection);
  };

  const updateProductToReactionMap = (nextProductToReactionMap: Record<string, number>) => {
    onChangeProductToReactionMap(nextProductToReactionMap);
  };

  const updateExpandedNodes = (nextExpandedNodes: string[]) => {
    onChangeExpandedNodes(nextExpandedNodes);
  };

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 't' || e.key === ' ') {
        toggleActive();
      }
    }

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [toggleActive]);

  if (parsingError) {
    return <ErrorMessage>Failed to display hypertree: {parsingError}</ErrorMessage>;
  }

  return (
    <Container>
      <TreeTools>
        <ZoomControls>
          Zoom: <i>{zoom.toFixed(1)}</i>
          <Slider
            value={[zoom]}
            onValueChange={value => {
              setZoom(value[0]);
            }}
            min={1}
            max={3}
            step={0.1}
          >
            <Slider.Track>
              <Slider.Range />
            </Slider.Track>
            <Slider.Thumb aria-label="Volume" />
          </Slider>
        </ZoomControls>

        <Checkbox
          id="expand-all-branches"
          label="Expand all branches"
          isChecked={expandAll}
          onCheckedChange={() => {
            setExpandAll(!expandAll);
          }}
        />
      </TreeTools>

      <TreeOverflowContainer>
        <TreeContainer css={{ width: zoom > 1 ? `${zoom * 100}%` : 'auto' }}>
          {!isTreeRenderable && 'No synthesis data loaded'}

          {isTreeRenderable && (
            <SynthesisTree
              id="tree"
              tree={nodes}
              smilesToMol={smilesToMol}
              debugEnabled={debugEnabled}
              skipCache={true}
              renderPointContext={{
                common: (props: CommonPointProps) => {
                  const { productId, reactionData, point, scale } = props;

                  const productNode = hypertree.nodes[productId];
                  const reactionCount = productNode.succ.length;
                  const currentReactionIdx = productToReactionMap[productId] ?? 0;

                  let reactionSmiles = '';
                  let reaction: Reaction | undefined;
                  try {
                    reaction = getReaction(productId, currentReactionIdx, hypertree);
                    reactionSmiles = `${reaction.substrates.join('.')}>>${reaction.product}`;
                  } catch (error) {
                    console.error('Failed to get reaction', productId, error);
                  }

                  const hasMultipleReactions = reactionCount > 1;

                  const xModifier = reactionData.substrateSmiles.length > 1 ? 0 : -20;
                  const commonPointProps = {
                    x: point[0] + xModifier,
                    y: point[1],
                    width: 60,
                    height: hasMultipleReactions ? 62 + (35 + 5) * 2 : 35,
                  };

                  return (
                    <foreignObject
                      x={commonPointProps.x - commonPointProps.width / 2}
                      y={commonPointProps.y - commonPointProps.height / 2}
                      width={commonPointProps.width}
                      height={commonPointProps.height}
                      style={{
                        transform: `scale(${scale}, ${scale})`,
                        transformOrigin: `${commonPointProps.x}px ${commonPointProps.y}px`,
                        overflow: 'visible',
                        textAlign: 'center',
                      }}
                    >
                      <ReactionCommonPointButtons
                        css={{
                          marginBottom: hasMultipleReactions ? 5 : 0,
                        }}
                      >
                        {reaction && (
                          <References
                            reactionSmiles={reactionSmiles}
                            reaction={reaction}
                            scifinderToken={scifinderToken}
                            smilesToMol={smilesToMol}
                            rrConfig={rrConfig}
                          />
                        )}
                        {reactionSmiles && (
                          <ButtonCopy
                            copy={reactionSmiles}
                            size="small"
                            weight="minimal"
                            icon="copy"
                            css={{
                              'backgroundColor': 'white',
                              '& > span': { fontSize: '16px !important' },
                            }}
                            tooltipContentProps={{
                              css: {
                                fontSize: '12px !important',
                                wordBreak: 'break-all',
                              },
                            }}
                          />
                        )}
                      </ReactionCommonPointButtons>
                      <PathButtonGroup
                        {...props}
                        reactionCount={reactionCount}
                        justUpdated={lastUpdatedReactionSelection === productId}
                        value={currentReactionIdx}
                        onChange={(productId, nextValue) => {
                          const nextProductToReactionMap = {
                            ...productToReactionMap,
                            [productId]: nextValue,
                          };
                          updateProductToReactionMap(nextProductToReactionMap);
                          updateExpandedNodes([...expandedNodes, productId]);
                          setLastUpdatedReactionSelection(productId);
                        }}
                      />
                    </foreignObject>
                  );
                },
                end: (props: any) => {
                  const { substrateId } = props;
                  const node = hypertree.nodes[substrateId];
                  const compound = hypertree.cmpds[node.id];
                  if (!compound.is_sm) {
                    return null;
                  }
                  return (
                    <SubstratePrice
                      {...props}
                      smiles={compound.smiles}
                      getPricing={getPricing}
                      onClick={compoundPricing => {
                        setViewedCompoundPricing(compoundPricing);
                      }}
                    />
                  );
                },
              }}
              renderMoleculeContext={(props: { bbox: BoundingBox; scale: number; moleculeId: string }) => {
                const { moleculeId } = props;

                if (!props.bbox.size) {
                  return null;
                }

                const node = hypertree.nodes[moleculeId];
                const compound = hypertree.cmpds[node?.id];
                const smiles = compound?.smiles;

                const isCollapsedLeaf = collapsedNodeIds.includes(moleculeId);
                const isLeafAndNotSm = node?.succ.length === 0 && !compound?.is_sm;

                const isRoot = node?.pred === null;
                const isRootAndSm = isRoot && compound?.is_sm;

                return (
                  <>
                    <foreignObject
                      x={props.bbox.min[0] - MOL_CONTAINER_PADDING / props.scale - 20}
                      y={props.bbox.min[1] - MOL_CONTAINER_PADDING / props.scale - 20}
                      width={(props.bbox.size[0] + MOL_CONTAINER_PADDING * 2) / props.scale + 40}
                      height={(props.bbox.size[1] + MOL_CONTAINER_PADDING * 2) / props.scale + 40}
                      style={{
                        transform: `scale(${props.scale}, ${props.scale})`,
                        transformOrigin: `${props.bbox.min[0]}px ${props.bbox.min[1]}px`,
                        overflow: 'visible',
                      }}
                    >
                      <ButtonCopy
                        copy={smiles}
                        size="small"
                        weight="minimal"
                        icon="copy"
                        css={{
                          'position': 'absolute',
                          'top': 0,
                          'left': '50%',
                          'transform': 'translateX(-50%)',
                          'backgroundColor': 'white',
                          '& > span': { fontSize: '16px !important' },
                        }}
                        tooltipContentProps={{
                          css: {
                            fontSize: '12px !important',
                            wordBreak: 'break-all',
                          },
                        }}
                      />
                      {isRootAndSm && getPricing && (
                        <RootPrice
                          {...props}
                          smiles={smiles}
                          getPricing={getPricing}
                          onClick={compoundPricing => {
                            setViewedCompoundPricing(compoundPricing);
                          }}
                        />
                      )}
                      {isCollapsedLeaf && (
                        <ExpandButton
                          {...props}
                          onClick={() => {
                            updateExpandedNodes([...expandedNodes, moleculeId]);
                          }}
                        />
                      )}
                      {onStartSearchFromLeaf && isLeafAndNotSm && (
                        <StartSearchFromLeaf
                          {...props}
                          onClick={() => {
                            onStartSearchFromLeaf(smiles);
                          }}
                        />
                      )}
                    </foreignObject>
                  </>
                );
              }}
            />
          )}
        </TreeContainer>
      </TreeOverflowContainer>

      <Pricing compoundPricing={viewedCompoundPricing} close={() => setViewedCompoundPricing(null)} />

      <Nav>
        {onChangeSelection && (
          <Checkbox
            id="selected-synthesis"
            label={isSynthesisSelected ? 'Selected synthesis' : 'Select synthesis'}
            isChecked={isSynthesisSelected}
            onCheckedChange={toggleActive}
          />
        )}

        <Button
          icon="restart_alt"
          text="Reset graph"
          onClick={() => {
            updateProductToReactionMap({});
            updateExpandedNodes([]);
          }}
          weight="minimal"
          size="small"
        />
      </Nav>
    </Container>
  );
}
